import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/admin';
import { User } from '../../types';
import { Table } from '../../components/ui/Table';
import { Button } from '../../components/ui/Button';
import { Modal, Input, Select, Alert } from '../../components/ui/Modal';
import { statusBadge } from '../../components/ui/Button';
import { formatDate, formatUSD } from '../../utils/formatters';
import { gs } from '../../styles/globalStyles';
import toast from 'react-hot-toast';
import { Ico } from '../../components/ui/Icon';

type ModalType = 'suspend' | 'message' | 'balance' | 'edit' | 'delete' | null;

const AdminUsers: React.FC = () => {
  const [users, setUsers]       = useState<User[]>([]);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState<User | null>(null);
  const [modal, setModal]       = useState<ModalType>(null);

  const [suspendMsg, setSuspendMsg]     = useState('');
  const [adminMsg, setAdminMsg]         = useState('');
  const [balanceAmt, setBalanceAmt]     = useState('');
  const [balanceWallet, setBalanceWallet] = useState<'real' | 'demo'>('real');
  const [editFirst, setEditFirst]       = useState('');
  const [editLast, setEditLast]         = useState('');
  const [editEmail, setEditEmail]       = useState('');
  const [editRole, setEditRole]         = useState('');

  useEffect(() => { loadUsers(); }, [search]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await adminService.getUsers({ search, per_page: 100 });
      setUsers(res.data.data || []);
    } catch {} finally { setLoading(false); }
  };

  const openModal = (user: User, m: ModalType) => {
    setSelected(user);
    setModal(m);
    if (m === 'edit') { setEditFirst(user.first_name); setEditLast(user.last_name); setEditEmail(user.email); setEditRole(user.role); }
    if (m === 'message') { setAdminMsg(user.admin_message || ''); }
    if (m === 'suspend') { setSuspendMsg(''); }
  };

  const handleSuspend = async () => {
    if (!selected) return;
    try {
      if (selected.status === 'active') {
        await adminService.suspendUser(selected.id, suspendMsg);
        toast.success(`@${selected.username} suspended`);
      } else {
        await adminService.activateUser(selected.id);
        toast.success(`@${selected.username} activated`);
      }
      setModal(null); loadUsers();
    } catch { toast.error('Failed'); }
  };

  const handleMessage = async () => {
    if (!selected) return;
    try {
      await adminService.sendMessage(selected.id, adminMsg);
      toast.success('Message sent to user');
      setModal(null); loadUsers();
    } catch { toast.error('Failed'); }
  };

  const handleClearMessage = async (user: User) => {
    try {
      await adminService.clearMessage(user.id);
      toast.success('Message cleared');
      loadUsers();
    } catch { toast.error('Failed'); }
  };

  const handleBalance = async () => {
    if (!selected) return;
    try {
      await adminService.adjustBalance(selected.id, { wallet_type: balanceWallet, amount: Number(balanceAmt) });
      toast.success('Balance adjusted');
      setModal(null); loadUsers();
    } catch (e: any) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const handleEdit = async () => {
    if (!selected) return;
    try {
      await adminService.updateUser(selected.id, { first_name: editFirst, last_name: editLast, email: editEmail, role: editRole });
      toast.success('User updated');
      setModal(null); loadUsers();
    } catch (e: any) { toast.error(e.response?.data?.message || 'Update failed'); }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await adminService.deleteUser(selected.id);
      toast.success(`@${selected.username} deleted`);
      setModal(null); loadUsers();
    } catch (e: any) { toast.error(e.response?.data?.message || 'Delete failed'); }
  };

  const handleResetDemo = async (user: User) => {
    try {
      await adminService.resetDemo(user.id);
      toast.success('Demo balance reset');
      loadUsers();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="p-3 md:p-6">
      <div className="mb-5 flex gap-2">
        <div className="relative flex-1">
          <Ico name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-3.5 h-3.5 pointer-events-none" />
          <input className={gs.input + ' pl-9 text-sm'} placeholder="Search name, email, username..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button onClick={loadUsers} className={gs.btnGhost + ' text-xs'}><Ico name="refresh" className="w-3.5 h-3.5" /></button>
      </div>

      <Table
        loading={loading}
        columns={[
          { key: 'user', label: 'User', render: r => (
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-primary text-[10px] font-bold shrink-0">{r.first_name?.[0]}</div>
              <div>
                <p className="text-white font-semibold text-xs">{r.first_name} {r.last_name}</p>
                <p className="text-gray-500 text-[10px]">@{r.username}</p>
              </div>
            </div>
          )},
          { key: 'email', label: 'Email', render: r => <span className="text-gray-400 text-[10px]">{r.email}</span> },
          { key: 'role', label: 'Role', render: r => statusBadge(r.role) },
          { key: 'status', label: 'Status', render: r => statusBadge(r.status) },
          { key: 'balance', label: 'Real Bal', render: r => <span className="font-mono text-[10px] text-primary">{formatUSD(r.real_wallet?.balance || 0)}</span> },
          { key: 'msg', label: 'Msg', render: r => r.admin_message
            ? <span className="text-[10px] text-yellow-400 truncate max-w-[80px] block" title={r.admin_message}>📢 {r.admin_message}</span>
            : <span className="text-gray-600 text-[10px]">—</span>
          },
          { key: 'actions', label: 'Actions', render: r => (
            <div className="flex gap-1 flex-wrap">
              {/* Edit */}
              <button onClick={() => openModal(r, 'edit')} className="p-1.5 rounded-lg text-blue-400 hover:bg-blue-500/10 transition-all" title="Edit user">
                <Ico name="save" className="w-3.5 h-3.5" />
              </button>
              {/* Suspend if active / Unsuspend if suspended */}
              {r.status === 'active' ? (
                <button onClick={() => openModal(r, 'suspend')} className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10 transition-all" title="Suspend user">
                  <Ico name="ban" className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button onClick={() => openModal(r, 'suspend')} className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition-all" title="Unsuspend user">
                  <Ico name="check" className="w-3.5 h-3.5" />
                </button>
              )}
              {/* Message OR Clear message */}
              {r.admin_message ? (
                <button onClick={() => handleClearMessage(r)} className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-all" title="Clear admin message">
                  <Ico name="close" className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button onClick={() => openModal(r, 'message')} className="p-1.5 rounded-lg text-yellow-400 hover:bg-yellow-500/10 transition-all" title="Send message">
                  <Ico name="message" className="w-3.5 h-3.5" />
                </button>
              )}
              {/* Balance */}
              <button onClick={() => openModal(r, 'balance')} className="p-1.5 rounded-lg text-purple-400 hover:bg-purple-500/10 transition-all" title="Adjust balance">
                <Ico name="coin" className="w-3.5 h-3.5" />
              </button>
              {/* Reset Demo */}
              <button onClick={() => handleResetDemo(r)} className="p-1.5 rounded-lg text-cyan-400 hover:bg-cyan-500/10 transition-all" title="Reset demo balance">
                <Ico name="refresh" className="w-3.5 h-3.5" />
              </button>
              {/* Delete */}
              <button onClick={() => openModal(r, 'delete')} className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-all" title="Delete user">
                <Ico name="delete" className="w-3.5 h-3.5" />
              </button>
            </div>
          )},
        ]}
        data={users}
        keyExtractor={r => r.id}
        emptyMessage="No users found"
      />

      {/* Edit User */}
      <Modal isOpen={modal === 'edit'} onClose={() => setModal(null)} title={`Edit @${selected?.username}`} size="md">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <Input label="First Name" value={editFirst} onChange={e => setEditFirst(e.target.value)} />
            <Input label="Last Name" value={editLast} onChange={e => setEditLast(e.target.value)} />
          </div>
          <Input label="Email" type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} />
          <Select label="Role" value={editRole} onChange={e => setEditRole(e.target.value)} options={[{ value: 'user', label: 'User' }, { value: 'admin', label: 'Admin' }]} />
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" fullWidth onClick={() => setModal(null)}>Cancel</Button>
            <Button fullWidth onClick={handleEdit} icon={<Ico name="save" className="w-3.5 h-3.5" />}>Save</Button>
          </div>
        </div>
      </Modal>

      {/* Suspend / Activate */}
      <Modal isOpen={modal === 'suspend'} onClose={() => setModal(null)} title={selected?.status === 'active' ? 'Suspend User' : 'Unsuspend User'}>
        {selected?.status === 'active' ? (
          <div className="flex flex-col gap-4">
            <p className="text-gray-400 text-sm">Suspend <strong className="text-white">@{selected?.username}</strong>? They will lose platform access.</p>
            <Input label="Reason / message to user (optional)" placeholder="e.g. Violation of terms..." value={suspendMsg} onChange={e => setSuspendMsg(e.target.value)} />
            <div className="grid grid-cols-2 gap-3">
              <Button variant="ghost" fullWidth onClick={() => setModal(null)}>Cancel</Button>
              <Button variant="danger" fullWidth onClick={handleSuspend} icon={<Ico name="ban" className="w-3.5 h-3.5" />}>Suspend</Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-gray-400 text-sm">Restore full access for <strong className="text-white">@{selected?.username}</strong>?</p>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="ghost" fullWidth onClick={() => setModal(null)}>Cancel</Button>
              <Button fullWidth onClick={handleSuspend} icon={<Ico name="check" className="w-3.5 h-3.5" />}>Unsuspend</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Send Message */}
      <Modal isOpen={modal === 'message'} onClose={() => setModal(null)} title="Send Admin Message">
        <div className="flex flex-col gap-4">
          <p className="text-gray-400 text-sm">Message will be prominently displayed to <strong className="text-white">@{selected?.username}</strong> on their dashboard and header.</p>
          <Input label="Message" placeholder="e.g. Please complete KYC verification..." value={adminMsg} onChange={e => setAdminMsg(e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" fullWidth onClick={() => setModal(null)}>Cancel</Button>
            <Button fullWidth onClick={handleMessage} icon={<Ico name="message" className="w-3.5 h-3.5" />}>Send</Button>
          </div>
        </div>
      </Modal>

      {/* Adjust Balance */}
      <Modal isOpen={modal === 'balance'} onClose={() => setModal(null)} title="Adjust Balance">
        <div className="flex flex-col gap-4">
          <p className="text-gray-400 text-sm">Adjust balance for <strong className="text-white">@{selected?.username}</strong>. Use negative to debit.</p>
          <Select label="Wallet" options={[{ value: 'real', label: 'Real Account' }, { value: 'demo', label: 'Demo Account' }]} value={balanceWallet} onChange={e => setBalanceWallet(e.target.value as 'real' | 'demo')} />
          <Input type="number" label="Amount (USD)" placeholder="+500 or -100" value={balanceAmt} onChange={e => setBalanceAmt(e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" fullWidth onClick={() => setModal(null)}>Cancel</Button>
            <Button fullWidth onClick={handleBalance} icon={<Ico name="coin" className="w-3.5 h-3.5" />}>Apply</Button>
          </div>
        </div>
      </Modal>

      {/* Delete */}
      <Modal isOpen={modal === 'delete'} onClose={() => setModal(null)} title="Delete User" size="sm">
        <div className="flex flex-col gap-4">
          <Alert type="error" message={`Permanently delete @${selected?.username}? This cannot be undone.`} />
          <div className="grid grid-cols-2 gap-3">
            <Button variant="ghost" fullWidth onClick={() => setModal(null)}>Cancel</Button>
            <Button variant="danger" fullWidth onClick={handleDelete} icon={<Ico name="delete" className="w-3.5 h-3.5" />}>Delete Forever</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminUsers;
